// ===================================================================================================
// InstantQuotesPage.jsx
// ---------------------------------------------------------------------------------------------------
// Instant Quotes top-level page with two in-page tabs: "Search" and "Saved".
// - Keeps per-tab (browser tab) state isolated via a per-tab session namespace (selectedTab id).
// - Persists lightweight UI state (menu selection and refresh toggle) into Redux M_State per tab.
// - Restores the same state when switching between application tabs.
// - Forces a soft refresh toggle whenever the route path changes to keep child views up to date.
// - Renames the outer app tab when the language or tab id changes.
// ---------------------------------------------------------------------------------------------------

import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import InstantQuotesSearchConfig from './InstantQuotesSearchConfig';
import IntantQuotesConfig from './IntantQuotesConfig';

// unified tabs styling 
import 'src/pages/tabsInpage.css';

//====================================================
// Merge a list of {key,value} into an existing list of {key,value}.
// Keeps only one entry per key, overwriting existing values.
//====================================================
const mergeKV = (arr, updates) => {
  const map = new Map(arr.map(({ key, value }) => [key, value])); // O(1) lookups
  updates.forEach(({ key, value }) => map.set(key, value));       // overwrite or insert
  return Array.from(map, ([key, value]) => ({ key, value }));      // back to array shape
};

//====================================================
// Main page component for Instant Quotes.
// Renders a side or top tab bar depending on viewport width.
// Persists per-tab UI state (menu and refresh) in Redux session storage.
//====================================================
export default function InstantQuotesPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const matches = useMediaQuery('(min-width:900px)'); // responsive breakpoint

  // Read the entire state slice; used to query sessions and selected tab
  const M_State = useSelector((state) => state.StateKeyValue);

  // Primitive id of the currently focused application tab (namespace for per-tab state)
  const selectedTab = useSelector((s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value);

  const location = useLocation();

  // Per-tab namespace seed
  const [id, setId] = useState(() => M_State.find((obj) => obj.key === 'selectedTab')?.value);

  // Local UI state stored per application tab
  const [menu, setMenu] = useState(1);       // 1=Search, 2=Saved
  const [refresh, setRefresh] = useState(false);

  // Keys we persist into the per-tab session store
  const sessionKeys = ['menu', 'refresh'];

  //========================================
  // Rename the outer app tab whenever language or selected tab changes.
  //========================================
  useEffect(() => {
    if (!selectedTab) return;
    renameTab(String(selectedTab), t('InstantQuotes'));
  }, [ selectedTab]);

  //========================================
  // Restore per-tab session snapshot on initial mount.
  //========================================
  useEffect(() => {
    const session = M_State.find((obj) => obj.key === id)?.value;
    if (session) {
      const m = session.find((x) => x.key === 'menu')?.value;
      const ref = session.find((x) => x.key === 'refresh')?.value;
      if (m) setMenu(m);
      if (ref !== undefined) setRefresh(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //========================================
  // When the focused app tab changes, adopt its persisted session state.
  //========================================
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      const session = M_State.find((obj) => obj.key === newId)?.value;
      if (session) {
        const m = session.find((x) => x.key === 'menu')?.value;
        const ref = session.find((x) => x.key === 'refresh')?.value;
        if (m) setMenu(m);
        if (ref !== undefined) setRefresh(ref);
      }
    }
  }, [M_State, id]);

  //========================================
  // Flip a boolean to signal children they should refresh when the route changes.
  //========================================
  useEffect(() => {
    setRefresh((r) => !r);
  }, [location.pathname]);

  //========================================
  // Persist per-tab session snapshot (menu, refresh) whenever they change.
  // The snapshot is stored under the current app tab id.
  //========================================
  useEffect(() => {
    if (!id) return;
    const current = M_State.find((o) => o.key === id)?.value || [];
    const values = { menu, refresh };
    const updates = sessionKeys.map((k) => ({ key: k, value: values[k] }));
    const merged = mergeKV(current, updates);
    dispatch(M_StateActions.addM_State({ key: id, value: merged }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, menu, refresh, dispatch]);

  // In-page tabs (left column on wide screens, top tabs on small screens)
  const renderTabs = [
    { id: 1, labelTab: t('InstantQuotesSearch'), iconTab: <i className="pi pi-search"></i> },
    { id: 2, labelTab: t('InstantQuotesSaved'),  iconTab: <i className="pi pi-bookmark"></i> },
  ];

  return (
    // Backward-compat bridge + unified layout classes (visual only)
    <div className="tabsInpage divGlobalSettings">
      <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>
        {t('InstantQuotes')}
      </div>

      {/* Standard body split: sidebar (tabs) + content (panel) */}
      <div className="tabsInpage__body">
        {/* Sidebar column (controlled by CSS: widths, separator, responsive) */}
        <div className="tabsInpage__sidebar">
          <MTabsInpage
            type={matches ? 'column' : 'tabs'}
            renderTabs={renderTabs}
            firstTabOpen={menu}
            onGetTab={(obj) => setMenu(obj.id)} // set selected in-page tab
            fixedArea={false}
          />
        </div>

        {/* Right side content: switch between Search and Saved panes */}
        <div className="tabsInpage__content settingsPageMainDiv">
          {menu === 1 && (
            <InstantQuotesSearchConfig
              roles={roles}
              onCreateTab={onCreateTab}
              toastMessages={toastMessages}
              refresh={refresh} // children can watch this to refetch
            />
          )}

          {menu === 2 && (
            <IntantQuotesConfig
              roles={roles}
              onCreateTab={onCreateTab}
              toastMessages={toastMessages}
              refresh={refresh} // children can watch this to refetch
            />
          )}
        </div>
      </div>
    </div>
  );
}
